import React, { useEffect, useState, useRef } from "react";
import api from "../../api/api";
import "../../css/Employee.css";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

jsPDF.API.autoTable = autoTable;

if (typeof jsPDF.API.autoTable === "undefined") {
  console.error("jsPDF autoTable plugin not loaded correctly.");
}

const OrderList = () => {
  const [orderData, setOrderData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [employeesPerPage, setEmployeesPerPage] = useState(10);
  const [showListFilter, setShowListFilter] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState(null);

  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAllOnPage, setSelectAllOnPage] = useState(false); // ✅ New state for "Select All"

  const listFilterRef = useRef(null);

  useEffect(() => {
    api
      .get("/get/order/list")
      .then((response) => {
        if (response.data.success) {
          setOrderData(response.data.data);
          setFilteredData(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching orders:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let dataToFilter = orderData;

    if (searchTerm.trim() !== "") {
      const search = searchTerm.toLowerCase();
      dataToFilter = orderData.filter((order) => {
        return (
          (order.order_id || "").toString().toLowerCase().includes(search) ||
          (order.product_quantity || "").toLowerCase().includes(search) ||
          (order.product_price || "").toLowerCase().includes(search) ||
          (order.other_customer || "").toLowerCase().includes(search) ||
          (order.customer || "").toLowerCase().includes(search) ||
          (order.status || "").toLowerCase().includes(search) ||
          (order.created_at || "").toLowerCase().includes(search) ||
          (order.product_id || "").toString().toLowerCase().includes(search) ||
          (order.product_name || "").toLowerCase().includes(search) ||
          (order.employee_id || "").toString().toLowerCase().includes(search) ||
          (order.employee_firstname || "").toLowerCase().includes(search) ||
          (order.employee_phone || "").toLowerCase().includes(search)
        );
      });
    }

    setFilteredData(dataToFilter);
    setCurrentPage(1);
  }, [searchTerm, orderData]);

  const indexOfLastEmployee = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees = filteredData.slice(
    indexOfFirstEmployee,
    indexOfLastEmployee
  );
  const totalPages = Math.ceil(filteredData.length / employeesPerPage);

  const handlePerPageChange = (value) => {
    setEmployeesPerPage(value);
    setShowListFilter(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        listFilterRef.current &&
        !listFilterRef.current.contains(event.target)
      ) {
        setShowListFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeZoomModal = () => {
    setZoomImageUrl(null);
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prevSelected) =>
      prevSelected.includes(orderId)
        ? prevSelected.filter((id) => id !== orderId)
        : [...prevSelected, orderId]
    );
  };

  const handleSelectAllOnPage = (e) => {
    const checked = e.target.checked;
    setSelectAllOnPage(checked);

    const allFilteredOrderIds = filteredData.map((order) => order.order_id);

    setSelectedOrders(checked ? allFilteredOrderIds : []);
  };

  const downloadPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(14);
    doc.text("Order Details", 40, 40);

    const headers = [
      [
        "#",
        "Order ID",
        "Firm Name",
        "Product Name",
        "Quantity",
        "Price",
        "Customer",
        "Other Customer",
        "Date",
        "Sales Name",
        "Sales Phone",
        "State",
        "Address",
        "Status",
      ],
    ];

    // Filter only selected orders
    const selectedData = filteredData.filter((order) =>
      selectedOrders.includes(order.order_id)
    );

    if (selectedData.length === 0) {
      alert("Please select at least one order to download.");
      return;
    }

    const data = selectedData.map((order, index) => [
      index + 1,
      order.order_id,
      order.firm_name,
      order.product_name,
      order.product_quantity,
      order.product_price,
      order.customer,
      order.other_customer || "N/A",
      order.created_at,
      order.employee_firstname,
      order.employee_phone,
      order.state,
      `${order.state}, ${order.firm_name}, ${order.address}`,
      order.status,
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 60,
      styles: {
        fontSize: 7,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
      },
    });

    doc.save("order_details.pdf");
  };

  return (
    <section className="employee-section">
      <div className="inner-content">
        <div className="filter-wrapper">
          <h2>Order Details</h2>
          <div className="right-filter">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div
              className="list-fliter tooltip-wrapper"
              ref={listFilterRef}
              data-tooltip="Filter by List"
            >
              <button onClick={() => setShowListFilter(!showListFilter)}>
                <i className="fa-solid fa-filter"></i>
              </button>
              {showListFilter && (
                <div className="filter-data">
                  {[10, 30, 50, 100].map((number) => (
                    <div
                      key={number}
                      className={`filter-option ${
                        employeesPerPage === number ? "selected" : ""
                      }`}
                      onClick={() => handlePerPageChange(number)}
                    >
                      {number}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button className="select-btn-001">
              {" "}
              <input
                type="checkbox"
                onChange={handleSelectAllOnPage}
                checked={
                  filteredData.length > 0 &&
                  filteredData.every((order) =>
                    selectedOrders.includes(order.order_id)
                  )
                }
              />{" "}
              {""} &nbsp;Select All
            </button>

            <button className="download-btn" onClick={downloadPDF}>
              <i className="fa-regular fa-file-pdf"></i>&nbsp;Download PDF
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>#</th>
                    <th>Order ID</th>
                    <th>Firm Name</th>
                    <th>Product Name</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Customer</th>
                    <th>Other Customer</th>
                    <th>Date</th>
                    <th>Sales Name</th>
                    <th>Sales Phone</th>
                    <th>State</th>
                    <th>Address</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEmployees.map((order, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.order_id)}
                          onChange={() => toggleOrderSelection(order.order_id)}
                        />
                      </td>
                      <td>{indexOfFirstEmployee + index + 1}</td>
                      <td className="check-in">{order.order_id}</td>
                      <td>{order.firm_name}</td>
                      <td>{order.product_name}</td>
                      <td className="p-day">{order.product_quantity}</td>
                      <td className="check-in">{order.product_price}</td>
                      <td>{order.customer}</td>
                      <td>{order.other_customer || "N/A"}</td>
                      <td className="check-in">{order.created_at}</td>
                      <td>{order.employee_firstname}</td>
                      <td className="phone">
                        <i className="fa-solid fa-phone"></i>&nbsp;{" "}
                        {order.employee_phone}
                      </td>
                      <td className="state">{order.state}</td>
                      <td className="address">
                        {order.state}, {order.firm_name}, {order.address}
                      </td>
                      <td
                        style={{
                          color:
                            order.status === "active"
                              ? "#28a745"
                              : order.status === "placed"
                              ? "#ff9d17"
                              : undefined,
                          fontWeight: "bold",
                        }}
                      >
                        <span className="status">{order.status || "-"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}

        {zoomImageUrl && (
          <div className="zoom-modal-overlay" onClick={closeZoomModal}>
            <div
              className="zoom-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-btn" onClick={closeZoomModal}>
                <i className="fa-solid fa-xmark"></i>
              </button>
              <img src={zoomImageUrl} alt="Zoomed" className="zoomed-image" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrderList;
